'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

const moneyData = [
  {
    id: 'money-total',
    label: 'Total Money Collected',
    value: 284930,
    icon: 'BanknotesIcon',
    color: 'bg-primary',
    textColor: 'text-white',
    subColor: 'text-white/70',
    iconBg: 'bg-white/15',
    description: 'All payments received to date',
    trend: '+18.4%',
    trendDir: 'up',
  },
  {
    id: 'money-pending',
    label: 'Pending Payments',
    value: 18420,
    icon: 'ClockIcon',
    color: 'bg-amber-50',
    textColor: 'text-amber-900',
    subColor: 'text-amber-600',
    iconBg: 'bg-amber-100',
    description: '24 invoices awaiting settlement',
    trend: '+$3,200',
    trendDir: 'up',
    alert: true,
  },
  {
    id: 'money-stored',
    label: 'Stored / In Escrow',
    value: 42800,
    icon: 'ArchiveBoxIcon',
    color: 'bg-violet-50',
    textColor: 'text-violet-900',
    subColor: 'text-violet-600',
    iconBg: 'bg-violet-100',
    description: 'Held pending service completion',
    trend: '14 bookings',
    trendDir: 'neutral',
  },
  {
    id: 'money-settled',
    label: 'Settled & Disbursed',
    value: 223710,
    icon: 'CheckCircleIcon',
    color: 'bg-emerald-50',
    textColor: 'text-emerald-900',
    subColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
    description: 'Successfully paid to service providers',
    trend: '+$28,400',
    trendDir: 'up',
  },
];

const recentTransactions = [
  { id: 'txn-001', ref: 'TXN-8821', customer: 'Rachel Kim', service: 'Skyline Penthouse Suite', amount: 4200, status: 'settled', date: 'Apr 22, 2026' },
  { id: 'txn-002', ref: 'TXN-8820', customer: 'David Okonkwo', service: 'Priya Sharma — Maid', amount: 112, status: 'pending', date: 'Apr 22, 2026' },
  { id: 'txn-003', ref: 'TXN-8819', customer: 'Samantha Torres', service: 'Premium Butler Service', amount: 340, status: 'stored', date: 'Apr 21, 2026' },
  { id: 'txn-004', ref: 'TXN-8818', customer: 'Marcus Webb', service: 'Garden View Studio', amount: 1450, status: 'settled', date: 'Apr 21, 2026' },
  { id: 'txn-005', ref: 'TXN-8817', customer: 'Yuki Tanaka', service: 'Spa & Wellness Session', amount: 220, status: 'pending', date: 'Apr 20, 2026' },
  { id: 'txn-006', ref: 'TXN-8816', customer: 'Ibrahim Al-Sayed', service: 'Luxury Car Service', amount: 475, status: 'settled', date: 'Apr 20, 2026' },
  { id: 'txn-007', ref: 'TXN-8815', customer: 'Preethi Nair', service: 'Grace Okonkwo — Maid', amount: 90, status: 'overdue', date: 'Apr 18, 2026' },
];

const statusStyle: Record<string, string> = {
  settled: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-700',
  stored: 'bg-violet-50 text-violet-700',
  overdue: 'bg-red-50 text-red-700',
};

export default function MoneyManagement() {
  const [settlingId, setSettlingId] = useState<string | null>(null);

  const handleSettle = async (txnId: string, ref: string) => {
    setSettlingId(txnId);
    // TODO: POST /api/payments/settle with txnId
    await new Promise((r) => setTimeout(r, 1200));
    setSettlingId(null);
    toast.success(`Payment ${ref} marked as settled.`);
  };

  return (
    <div className="bg-white rounded-xl border border-border shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-xl">
            <Icon name="CurrencyDollarIcon" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-base text-foreground">Money Management</h2>
            <p className="text-xs text-muted-foreground">Financial overview and transaction control</p>
          </div>
        </div>
        <button
          onClick={() => toast.success('Financial report exported to CSV.')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        >
          <Icon name="ArrowDownTrayIcon" size={15} />
          Export
        </button>
      </div>

      {/* Money cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-border border-b border-border">
        {moneyData.map((item) => (
          <div key={item.id} className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-xl ${item.iconBg}`}>
                <Icon name={item.icon as 'BanknotesIcon'} size={18} className={item.textColor} />
              </div>
              {item.alert && (
                <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  Needs action
                </span>
              )}
            </div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{item.label}</p>
            <p className={`font-display font-bold text-2xl tabular-nums mb-1 ${item.textColor}`}>
              ${item.value.toLocaleString()}
            </p>
            <p className={`text-xs ${item.subColor}`}>{item.description}</p>
            {item.trendDir !== 'neutral' && (
              <p className={`text-xs font-medium mt-1 ${item.trendDir === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                {item.trendDir === 'up' ? '↑' : '↓'} {item.trend} this month
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Recent transactions */}
      <div className="px-6 py-4">
        <h3 className="font-display font-semibold text-sm text-foreground mb-4">Recent Transactions</h3>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-border">
                {['Reference', 'Customer', 'Service', 'Amount', 'Status', 'Date', 'Action'].map((h) => (
                  <th key={`txn-th-${h}`} className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pb-2 text-left pr-4 last:pr-0">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentTransactions.map((txn) => (
                <tr key={txn.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors group">
                  <td className="py-3 pr-4">
                    <span className="text-xs font-mono font-semibold text-primary">{txn.ref}</span>
                  </td>
                  <td className="py-3 pr-4 text-sm text-foreground font-medium whitespace-nowrap">{txn.customer}</td>
                  <td className="py-3 pr-4 text-sm text-muted-foreground max-w-[180px] truncate">{txn.service}</td>
                  <td className="py-3 pr-4 text-sm font-bold text-foreground tabular-nums">${txn.amount.toLocaleString()}</td>
                  <td className="py-3 pr-4">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${statusStyle[txn.status]}`}>
                      {txn.status}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-xs text-muted-foreground whitespace-nowrap">{txn.date}</td>
                  <td className="py-3">
                    {(txn.status === 'pending' || txn.status === 'overdue') ? (
                      <button
                        onClick={() => handleSettle(txn.id, txn.ref)}
                        disabled={settlingId === txn.id}
                        className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-lg hover:bg-primary-light transition-colors disabled:opacity-60 flex items-center gap-1"
                      >
                        {settlingId === txn.id ? (
                          <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                        ) : (
                          <Icon name="CheckIcon" size={12} />
                        )}
                        Settle
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}